import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiHelpCircle,
  FiMessageCircle,
  FiMail,
  FiCheckCircle,
  FiArrowLeft,
  FiChevronDown,
  FiChevronRight,
  FiShield,
  FiFileText,
} from 'react-icons/fi';
import { ThemeContext } from '../context/ThemeContext';
import MarketingLayout from '../layouts/MarketingLayout';
import { SUPPORT_EMAIL } from '../config/emailjs';

const FAQS = [
  {
    id: 'para-quien',
    q: '¿Para quién está pensada Contsuite?',
    a: 'Para emprendedores y pymes que quieren cobrar más rápido, saber a quién cobrar primero y llevarle orden al contador. Registro interno de ventas y gastos, sin facturación electrónica ARCA.',
  },
  {
    id: 'contabilidad',
    q: '¿Necesito saber de contabilidad?',
    a: 'No. Las pantallas están pensadas para quien solo quiere saber quién le debe, cuánto cobró y cómo viene el mes.',
  },
  {
    id: 'probar',
    q: '¿Puedo probar sin compromisos?',
    a: 'Sí. Podés crear tu cuenta, cargar clientes y ventas, y decidir más adelante si seguís. Al registrarte aceptás nuestros términos y la política de privacidad.',
  },
  {
    id: 'planes',
    q: '¿Qué pasa si cambio de plan?',
    a: 'Podés subir o bajar cuando lo necesites. Tus datos se mantienen; cambian las funciones habilitadas (equipo, auditoría, presupuestos, etc.).',
  },
  {
    id: 'seguridad',
    q: '¿Mis datos están seguros?',
    a: 'Usamos HTTPS y almacenamiento en la nube con buenas prácticas de seguridad. Solo vos y quien invites a tu cuenta pueden ver tu información.',
  },
];

const Ayuda = () => {
  const { modoOscuro } = useContext(ThemeContext);
  const [openId, setOpenId] = useState(FAQS[0].id);

  const pageBg = modoOscuro ? 'bg-black' : 'bg-[#f5f5f7]';
  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-700';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const chipBg = modoOscuro ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200';

  const toggleFaq = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  const legalLinks = useMemo(
    () => [
      { to: '/terminos', label: 'Términos de uso', icon: FiFileText },
      { to: '/privacidad', label: 'Política de privacidad', icon: FiShield },
    ],
    []
  );

  return (
    <MarketingLayout>
      <div className={pageBg}>
        <section className="pt-10 pb-8 sm:pt-14 sm:pb-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/"
              className={`inline-flex items-center gap-2 text-sm mb-8 ${textMuted} hover:text-[#1F80FF] transition-colors`}
            >
              <FiArrowLeft className="w-4 h-4" />
              Volver al inicio
            </Link>

            <div className="flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-6">
              <div
                className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
                  modoOscuro ? 'bg-[#1F80FF]/15 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#1F80FF]'
                }`}
              >
                <FiHelpCircle className="w-7 h-7" aria-hidden />
              </div>
              <div className="min-w-0 flex-1 text-left sm:text-center lg:text-left">
                <p className={`text-[11px] font-semibold tracking-[0.2em] uppercase mb-2 ${textMuted}`}>
                  AYUDA RÁPIDA
                </p>
                <h1 className={`text-3xl sm:text-4xl font-semibold leading-tight mb-3 ${textPri}`}>
                  Preguntas simples para usar Contsuite
                </h1>
                <p className={`text-sm sm:text-base leading-relaxed max-w-2xl sm:mx-auto lg:mx-0 ${textSec}`}>
                  Respuestas cortas, sin jerga contable. Si algo no está acá, escribinos y te orientamos.
                </p>
                <div className="flex flex-wrap gap-2 mt-5 sm:justify-center lg:justify-start">
                  <span className={`text-[11px] font-medium px-3 py-1 rounded-full border ${chipBg} ${textSec}`}>
                    {FAQS.length} temas frecuentes
                  </span>
                  <Link
                    to="/funcionalidades"
                    className={`text-[11px] font-medium px-3 py-1 rounded-full border ${chipBg} ${textSec} hover:text-[#1F80FF]`}
                  >
                    Ver funcionalidades
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-14 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-8 lg:gap-10 items-start">
              <div className="space-y-3 min-w-0">
                {FAQS.map((item) => {
                  const isOpen = openId === item.id;
                  return (
                    <div key={item.id} className={`border rounded-2xl overflow-hidden ${box}`}>
                      <button
                        type="button"
                        id={item.id}
                        className={`w-full flex items-start gap-3 p-4 sm:p-5 text-left transition-colors ${
                          isOpen ? (modoOscuro ? 'bg-gray-800/50' : 'bg-gray-50/80') : ''
                        }`}
                        aria-expanded={isOpen}
                        onClick={() => toggleFaq(item.id)}
                      >
                        <div
                          className={`mt-0.5 w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                            modoOscuro ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          <FiHelpCircle className="w-4 h-4" />
                        </div>
                        <span className="flex-1 min-w-0">
                          <span className={`block text-sm sm:text-base font-semibold ${textPri}`}>{item.q}</span>
                        </span>
                        {isOpen ? (
                          <FiChevronDown className={`w-5 h-5 shrink-0 mt-0.5 ${textMuted}`} />
                        ) : (
                          <FiChevronRight className={`w-5 h-5 shrink-0 mt-0.5 ${textMuted}`} />
                        )}
                      </button>
                      {isOpen && (
                        <div className={`px-4 sm:px-5 pb-4 sm:pb-5 pt-0 pl-[3.25rem] sm:pl-[3.75rem] ${textSec}`}>
                          <p className="text-sm leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <aside className="space-y-4 lg:sticky lg:top-28">
                <div className={`rounded-2xl border p-5 space-y-4 ${box}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-[#1F80FF]/10 text-[#1F80FF]">
                      <FiMessageCircle className="w-4 h-4" />
                    </div>
                    <h2 className={`text-sm sm:text-base font-semibold ${textPri}`}>¿Seguís con dudas?</h2>
                  </div>
                  <p className={`text-xs sm:text-sm leading-relaxed ${textSec}`}>
                    Contanos tu caso (monotributo, cuántos clientes, si tenés contador). Te respondemos en lenguaje claro.
                  </p>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Consulta desde Ayuda')}`}
                    className="inline-flex items-center gap-2 text-sm text-[#1F80FF] hover:underline break-all"
                  >
                    <FiMail className="w-4 h-4 shrink-0" />
                    {SUPPORT_EMAIL}
                  </a>
                  <div className="flex items-start gap-2 text-xs sm:text-sm">
                    <FiCheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    <span className={textSec}>Respuesta habitual en menos de 24 h hábiles.</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('openContactModal'))}
                    className="w-full text-center px-3 py-2.5 rounded-xl text-xs font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] transition-colors"
                  >
                    Abrir formulario de contacto
                  </button>
                </div>

                <div className={`rounded-2xl border p-4 space-y-2 ${box}`}>
                  <p className={`text-[11px] font-semibold uppercase tracking-wider mb-1 ${textMuted}`}>
                    Legal y planes
                  </p>
                  {legalLinks.map(({ to, label, icon: Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      className={`flex items-center justify-between gap-2 text-sm rounded-xl px-3 py-2.5 border transition-colors ${
                        modoOscuro
                          ? 'border-gray-700 hover:bg-gray-800 text-[#5CA3FF]'
                          : 'border-gray-200 hover:bg-gray-50 text-[#1F80FF]'
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <Icon className="w-4 h-4 opacity-80" />
                        {label}
                      </span>
                      <FiChevronRight className="w-4 h-4 opacity-60" />
                    </Link>
                  ))}
                  <Link
                    to="/planes"
                    className={`flex items-center justify-between gap-2 text-sm rounded-xl px-3 py-2.5 border transition-colors ${textSec} ${
                      modoOscuro ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    Planes y precios
                    <FiChevronRight className="w-4 h-4 opacity-60" />
                  </Link>
                </div>

                <div className="text-[11px] leading-relaxed rounded-xl px-3 py-2.5 bg-[#1F80FF]/10 text-[#1F80FF] border border-[#1F80FF]/20">
                  Consejo: empezá solo con clientes y ventas del mes. El resto lo podés sumar de a poco.
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default Ayuda;
